import { LESSONS, MODULES } from '@/data/lessons';
import type { ProgressMap } from '@/hooks/useProgress';

/**
 * Regras de domínio e progressão do curso.
 *
 * O curso é sequencial: a aula 1 está sempre liberada e cada aula só libera a
 * seguinte depois de ser dominada. Um módulo é considerado concluído quando
 * TODAS as suas aulas foram dominadas.
 */

/**
 * Combo de acertos usado como elemento de gamificação.
 * Ele dá feedback positivo ao aluno, mas não desbloqueia sozinho a próxima etapa.
 */
export const STREAK_GOAL = 20;

/** Precisão mínima exigida para considerar um conteúdo realmente dominado. */
export const MASTERY_ACCURACY_GOAL = 98;

/**
 * Quantidade mínima de variações respondidas antes de permitir domínio nas
 * atividades de reconhecimento. Com 50 tentativas, 1 erro ainda permite 98%;
 * quem erra mais continua praticando até recuperar o índice.
 */
export const MASTERY_MIN_ATTEMPTS = 50;

/** Meta de domínio também usada nas execuções de ritmo e melodia. */
export const RHYTHM_ACCURACY_GOAL = MASTERY_ACCURACY_GOAL;

/**
 * Mantido por compatibilidade: o novo modelo aceita raríssimos erros desde que
 * cada item medido da habilidade termine com pelo menos 98% de precisão.
 */
export const RHYTHM_MASTERY_REQUIRES_ERROR_FREE = false;

/** A aula foi dominada pelo aluno (concluída com o critério da sua categoria)? */
export function isLessonMastered(progress: ProgressMap, lessonId: number): boolean {
  return progress[String(lessonId)]?.mastered === true;
}

/**
 * A aula está desbloqueada?
 * - A aula 1 está sempre desbloqueada.
 * - Uma aula dominada continua acessível (permite refazer).
 * - Caso contrário, é preciso ter dominado a aula imediatamente anterior.
 */
export function isLessonUnlocked(progress: ProgressMap, lessonId: number): boolean {
  if (lessonId <= 1) return true;
  if (isLessonMastered(progress, lessonId)) return true;
  return isLessonMastered(progress, lessonId - 1);
}

/** O módulo está concluído? (todas as suas aulas dominadas) */
export function isModuleComplete(progress: ProgressMap, moduleId: number): boolean {
  const module = MODULES.find((item) => item.id === moduleId);
  if (!module) return false;
  return module.lessonIds.every((id) => isLessonMastered(progress, id));
}

/** O módulo já foi desbloqueado? (basta a primeira aula dele estar acessível) */
export function isModuleUnlocked(progress: ProgressMap, moduleId: number): boolean {
  const module = MODULES.find((item) => item.id === moduleId);
  if (!module || module.lessonIds.length === 0) return true;
  return isLessonUnlocked(progress, module.lessonIds[0]);
}

/** Quantas aulas já foram dominadas no curso inteiro. */
export function masteredCount(progress: ProgressMap): number {
  return LESSONS.filter((lesson) => isLessonMastered(progress, lesson.id)).length;
}